import React, { useEffect, useState } from "react";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Loader } from "@design-system/Loader/Loader";
import { PageCoverLoader } from "@design-system/PageCoverLoader/PageCoverLoader";
import { Skeleton } from "@design-system/Skeleton/Skeleton";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { BooleanControl, VariantGrid, VariantItem } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./LoadingPlayground.module.scss";
import gridStyles from "./playgroundControls.module.scss";

type ShowcaseSize = "sm" | "md" | "default" | "lg";

const showcaseSizes: readonly ShowcaseSize[] = ["sm", "md", "default", "lg"];

const loaderButtonSizes = {
    sm: "sm",
    md: "md",
    default: "lg",
    lg: "xl",
} as const satisfies Record<ShowcaseSize, "sm" | "md" | "lg" | "xl">;

const getSkeletonDimensions = (size: ShowcaseSize) => {
    switch (size) {
        case "sm":
            return { avatar: 32, line: 96, cardHeight: 56 };
        case "md":
            return { avatar: 40, line: 128, cardHeight: 72 };
        case "lg":
            return { avatar: 56, line: 192, cardHeight: 112 };
        default:
            return { avatar: 48, line: 160, cardHeight: 88 };
    }
};

export const LoadingPlayground = () => {
    const [size, setSize] = useState<ShowcaseSize>("default");
    const [isButtonLoading, setIsButtonLoading] = useState(true);
    const [hasBackdrop, setHasBackdrop] = useState(true);
    const [hasLogo, setHasLogo] = useState(true);
    const [isPageCoverVisible, setIsPageCoverVisible] = useState(false);
    const skeletonDimensions = getSkeletonDimensions(size);

    useEffect(() => {
        if (!isPageCoverVisible) {
            return;
        }

        const timeoutId = window.setTimeout(
            () => setIsPageCoverVisible(false),
            2_000,
        );

        return () => window.clearTimeout(timeoutId);
    }, [isPageCoverVisible]);

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Skeleton and button size</h2>
                        <ToggleGroup.Root<ShowcaseSize>
                            value={size}
                            onValueChange={(value) => {
                                if (value) {
                                    setSize(value);
                                }
                            }}
                        >
                            {showcaseSizes.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </section>
                    <section>
                        <h2>Loading button</h2>
                        <BooleanControl
                            label="Loading"
                            checked={isButtonLoading}
                            onChange={setIsButtonLoading}
                        />
                    </section>
                    <section>
                        <h2>Page cover</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Backdrop"
                                checked={hasBackdrop}
                                onChange={setHasBackdrop}
                            />
                            <BooleanControl
                                label="Logo"
                                checked={hasLogo}
                                onChange={setHasLogo}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width="full"
                output={JSON.stringify(
                    {
                        componentProps: {
                            loader: {
                                inlineVariant: { inline: true },
                                defaultVariant: { inline: false },
                            },
                            skeleton: {
                                textLine: {
                                    width: skeletonDimensions.line,
                                    height: 16,
                                },
                                circle: {
                                    width: skeletonDimensions.avatar,
                                    height: skeletonDimensions.avatar,
                                    className:
                                        "playground-local circular style",
                                },
                                contentBlock: {
                                    width: "100%",
                                    height: skeletonDimensions.cardHeight,
                                },
                            },
                            buttonWithLoader: {
                                size: loaderButtonSizes[size],
                                loading: isButtonLoading,
                            },
                            pageCoverLoader: {
                                backdrop: hasBackdrop,
                                logo: hasLogo,
                            },
                        },
                        showcaseState: {
                            selectedSkeletonAndButtonSize: size,
                            skeletonCircle: "composed with local border-radius",
                            isPageCoverVisible,
                        },
                    },
                    null,
                    2,
                )}
            >
                <div className={gridStyles.fillWidth}>
                    <VariantGrid title="Loader sizes" columns="auto">
                        <VariantItem label="inline">
                            <Loader inline />
                        </VariantItem>
                        <VariantItem label="default">
                            <Loader />
                        </VariantItem>
                    </VariantGrid>

                    <VariantGrid title="Skeleton shapes" columns="wide">
                        <VariantItem label={`text line · ${size}`}>
                            <Skeleton
                                width={skeletonDimensions.line}
                                height={16}
                            />
                        </VariantItem>
                        <VariantItem
                            label={`circle via local border-radius · ${size}`}
                        >
                            <Skeleton
                                className={styles.circularSkeleton}
                                width={skeletonDimensions.avatar}
                                height={skeletonDimensions.avatar}
                            />
                        </VariantItem>
                        <VariantItem label={`content block · ${size}`}>
                            <Skeleton
                                width="100%"
                                height={skeletonDimensions.cardHeight}
                            />
                        </VariantItem>
                    </VariantGrid>

                    <VariantGrid title="Button with loader" columns="wide">
                        <VariantItem label={`loading: ${isButtonLoading}`}>
                            <ButtonWithLoader
                                type="button"
                                variant="primary"
                                size={loaderButtonSizes[size]}
                                loading={isButtonLoading}
                            >
                                Save changes
                            </ButtonWithLoader>
                        </VariantItem>
                    </VariantGrid>

                    <VariantGrid title="Page cover loader" columns="full">
                        <VariantItem label="full-screen · visible for 2 seconds">
                            <Button
                                type="button"
                                variant="primary"
                                size="md"
                                onClick={() => setIsPageCoverVisible(true)}
                            >
                                Show for 2 seconds
                            </Button>
                        </VariantItem>
                    </VariantGrid>
                </div>
            </ShowcasePreview>

            {isPageCoverVisible ? (
                <PageCoverLoader backdrop={hasBackdrop} logo={hasLogo}>
                    Loading your workspace
                </PageCoverLoader>
            ) : null}
        </SandboxLayout>
    );
};
